import { inject, Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { IAiReport } from '../../../../shared/components/ai-report-view/ai-report-view.interface';
import { ELoadStatus } from '../../../../state/state.interface';
import { IBaseResponse } from '../../../../utilities/http-utilities.service';
import { IAiAssistRequest, IAiAssistSuggestion, IItemAiReportRequest } from './ai-assist-dialog.interface';
import { AiAssistDialogService } from './ai-assist-dialog.service';

export interface IAiAssistDialogState {
  status: ELoadStatus;
  suggestion: IAiAssistSuggestion | null;
  report: IAiReport | null;
}

const INITIAL_STATE: IAiAssistDialogState = { report: null, status: ELoadStatus.initial, suggestion: null };

@Injectable()
export class AiAssistDialogStore extends ComponentStore<IAiAssistDialogState> {
  private readonly service = inject(AiAssistDialogService);

  public readonly isLoading: Signal<boolean> = this.selectSignal((state) => state.status === ELoadStatus.loading);
  public readonly suggestion: Signal<IAiAssistSuggestion | null> = this.selectSignal((state) => state.suggestion);
  /** The report mode's answer; null until the agent has answered. */
  public readonly report: Signal<IAiReport | null> = this.selectSignal((state) => state.report);

  /** Errors are shown by the global HTTP error handling; the dialog just goes back to the request form. */
  public readonly requestSuggestion = this.effect((trigger$: Observable<IAiAssistRequest>) =>
    trigger$.pipe(
      switchMap((request: IAiAssistRequest) => {
        this.setState({ ...INITIAL_STATE, status: ELoadStatus.loading });

        return this.service.requestSuggestion(request).pipe(
          tapResponse(
            (response: IBaseResponse<IAiAssistSuggestion>) =>
              this.patchState({ status: ELoadStatus.success, suggestion: response.data }),
            () => this.patchState({ status: ELoadStatus.failure }),
          ),
        );
      }),
    ),
  );

  /** Same error handling as the suggestion. */
  public readonly requestReport = this.effect(
    (trigger$: Observable<{ itemId: number; request: IItemAiReportRequest }>) =>
      trigger$.pipe(
        switchMap(({ itemId, request }) => {
          this.setState({ ...INITIAL_STATE, status: ELoadStatus.loading });

          return this.service.requestReport(itemId, request).pipe(
            tapResponse(
              (response: IBaseResponse<IAiReport>) =>
                this.patchState({ report: response.data, status: ELoadStatus.success }),
              () => this.patchState({ status: ELoadStatus.failure }),
            ),
          );
        }),
      ),
  );

  constructor() {
    super(INITIAL_STATE);
  }

  public readonly clearResult = (): void => this.setState(INITIAL_STATE);
}
